import React, { useCallback, useEffect, useRef } from "react";

import {
  QBodyLayout,
  QButton,
  QFooter,
  QHeader,
  QStep,
  QStepperProgress,
  QTitle,
} from "@qualio/ui-components";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import { StepBlocker } from "../../components/StepBlocker/StepBlocker";
import { CreateProductProvider } from "../../context/CreateProductContext";
import { useCreateProductStore } from "../../hooks/useCreateProductStore";
import { Product, Requirements, Risk, TestCases } from "./steps";
import { useCreateProduct } from "../../hooks/createProductSteps/useCreateProduct";

export const CreateProductLayout: React.FC = () => {
  const { t } = useTranslation();
  const nextRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const { activeStep, isDisabled, isComplete, previousActiveStep } =
    useCreateProductStore((state) => ({
      activeStep: state.activeStep,
      isDisabled: state.isDisabled,
      isComplete: state.isComplete,
      previousActiveStep: state.previousActiveStep,
    }));

  const { isLoading, createProduct } = useCreateProduct();

  const onCancel = useCallback(() => {
    navigate("/product");
  }, [navigate]);

  useEffect(() => {
    isComplete && createProduct();
  }, [isComplete]);

  return (
    <QBodyLayout.SplitPanelLeftWithStepper
      size="md"
      activeStep={activeStep}
      onStepItemClicked={() => undefined}
    >
      <QHeader>
        <QTitle>{t("products.create.title")}</QTitle>
      </QHeader>
      <QStepperProgress />
      <QStep title={t("products.create.steps.productProfile")} />
      <QStep title={t("products.create.steps.requirements")} />
      <QStep title={t("products.create.steps.testCases")} />
      <QStep title={t("products.create.steps.risks")} />
      <StepBlocker isActive={activeStep === 0}>
        <Product nextRef={nextRef} />
      </StepBlocker>
      <StepBlocker isActive={activeStep === 1}>
        <Requirements nextRef={nextRef} />
      </StepBlocker>
      <StepBlocker isActive={activeStep === 2}>
        <TestCases nextRef={nextRef} />
      </StepBlocker>
      <StepBlocker isActive={activeStep === 3}>
        <Risk nextRef={nextRef} />
      </StepBlocker>
      <QFooter>
        <QButton variant="ghost" onClick={onCancel} data-cy="cancel">
          {t("products.create.actions.cancel")}
        </QButton>
        <QButton
          rightIcon="ArrowRight"
          onClick={() => nextRef.current?.click()}
          isDisabled={isDisabled}
          data-cy="submit-button"
        >
          {t("products.create.actions.next")}
        </QButton>
      </QFooter>
      <QFooter>
        <QButton variant="ghost" onClick={onCancel} data-cy="cancel">
          {t("products.create.actions.cancel")}
        </QButton>
        <QButton
          leftIcon="ArrowLeft"
          onClick={previousActiveStep}
          variant="outline"
          data-cy="previous-button"
        >
          {t("products.create.actions.back")}
        </QButton>
        <QButton
          rightIcon="ArrowRight"
          onClick={() => nextRef.current?.click()}
          isDisabled={isDisabled}
          data-cy="submit-button"
        >
          {t("products.create.actions.next")}
        </QButton>
      </QFooter>
      <QFooter>
        <QButton variant="ghost" onClick={onCancel} data-cy="cancel">
          {t("products.create.actions.cancel")}
        </QButton>
        <QButton
          leftIcon="ArrowLeft"
          onClick={previousActiveStep}
          variant="outline"
          data-cy="previous-button"
        >
          {t("products.create.actions.back")}
        </QButton>
        <QButton
          rightIcon="ArrowRight"
          onClick={() => nextRef.current?.click()}
          isDisabled={isDisabled}
          data-cy="submit-button"
        >
          {t("products.create.actions.next")}
        </QButton>
      </QFooter>
      <QFooter>
        <QButton variant="ghost" onClick={onCancel} data-cy="cancel">
          {t("products.create.actions.cancel")}
        </QButton>
        <QButton
          leftIcon="ArrowLeft"
          onClick={previousActiveStep}
          variant="outline"
          data-cy="previous-button"
          isDisabled={isLoading}
        >
          {t("products.create.actions.back")}
        </QButton>
        <QButton
          rightIcon="ArrowRight"
          onClick={() => {
            nextRef.current?.click();
          }}
          isLoading={isLoading}
          data-cy="create-product-button"
        >
          {t("products.create.actions.createProduct")}
        </QButton>
      </QFooter>
    </QBodyLayout.SplitPanelLeftWithStepper>
  );
};

export const CreateProduct: React.FC = () => {
  return (
    <CreateProductProvider>
      <CreateProductLayout />
    </CreateProductProvider>
  );
};
